import { LODGE_EMAIL, LODGE_PHONE_DISPLAY, LODGE_PHONE_SCHEMA, callAriaLabel, telHref } from "@/lib/contact-info";
import { createFileRoute, Link } from "@tanstack/react-router";
import { seoHead, DEFAULT_OG_IMAGE } from "@/lib/seo";
import roomsAsset from "@/assets/rooms-family-units.jpg.asset.json";

import truckAsset from "@/assets/vehicle-rental-truck.png.asset.json";
import boatSkiffAsset from "@/assets/hewes-craft-boat.png.asset.json";
import skiffAsset from "@/assets/skiff-rental.jpg.asset.json";



const ACCOMMODATIONS_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "LodgingBusiness",
  "@id": "https://coffmancovelodging.com/#lodging",
  name: "Coffman Cove Lodging",
  url: "https://coffmancovelodging.com/accommodations",
  telephone: LODGE_PHONE_SCHEMA,
  email: LODGE_EMAIL,
  priceRange: "$$",
  image: DEFAULT_OG_IMAGE,
  address: {
    "@type": "PostalAddress",
    streetAddress: "306 Harbor Ave",
    addressLocality: "Coffman Cove",
    addressRegion: "AK",
    postalCode: "99918",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 56.0147,
    longitude: -132.8339,
  },
  amenityFeature: [
    { "@type": "LocationFeatureSpecification", name: "Boat rentals", value: true },
    { "@type": "LocationFeatureSpecification", name: "Vehicle rentals", value: true },
    { "@type": "LocationFeatureSpecification", name: "Fish cleaning & processing", value: true },
    { "@type": "LocationFeatureSpecification", name: "Homestyle meals", value: true },
    { "@type": "LocationFeatureSpecification", name: "Wi-Fi", value: true },
    { "@type": "LocationFeatureSpecification", name: "Private bath", value: true },
  ],
  makesOffer: [
    {
      "@type": "Offer",
      name: "Rooms & Family Units",
      priceCurrency: "USD",
      price: "200",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: "200",
        priceCurrency: "USD",
        unitCode: "DAY",
      },
    },
    {
      "@type": "Offer",
      name: "Boat Rental (Hewescraft)",
      priceCurrency: "USD",
      price: "400",
      description: "20' / 22' welded aluminum Hewescraft boats, fuel extra.",
    },
    {
      "@type": "Offer",
      name: "Skiff Rental",
      priceCurrency: "USD",
      price: "100",
    },
    {
      "@type": "Offer",
      name: "Vehicle Rental (4x4 Pickup)",
      priceCurrency: "USD",
      price: "85",
    },
  ],
};

export const Route = createFileRoute("/accommodations")({
  head: () =>
    seoHead({
      path: "/accommodations",
      title: "Stay, Rentals & Services — Coffman Cove Lodging",
      description:
        "Rooms, boat & vehicle rentals, fish processing and island transportation for self-guided fishing at Coffman Cove Lodging, Alaska.",
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(ACCOMMODATIONS_JSON_LD),
        },
      ],
    }),
  component: Accommodations,
});


const PACKAGES = [
  {
    img: roomsAsset.url,
    title: "Rooms & Family Units",
    price: "$200 / night",
    features: [
      "Homestyle breakfast & dinner",
      "Sack lunch for the boat",
      "Washer / dryer access",
      "Wi-Fi & private bath",
    ],
  },
  {
    img: boatSkiffAsset.url,
    title: "Boat & Skiff Rentals",
    price: "Boats $400 / day + fuel · Skiff $100 / day",
    features: [
      "20' / 22' welded aluminum Hewescraft boats",
      "16ft welded aluminum skiffs",
      "Downriggers, GPS / sonar, VHF radio",
      "Bait, ice & a tank of gas",
      "Kayaks also available",
    ],
  },
  {
    img: truckAsset.url,
    title: "Vehicle Rentals",
    price: "$85 / day · $450 / week",
    features: [
      "4x4 Chevy pickup ready at the lodge",
      "Explore the Prince of Wales Island road system",
      "Bed for coolers, gear & fish boxes",
      "Based on availability — fuel not included",
    ],
  },
];

const SERVICES = [
  {
    title: "Fish Filleting, Vacuum Sealing & Freezing",
    price: "$3 / lb · approx. $150 / box",
    body: "Done by Hedges Marine Processing. Walk-in freezer on site.",
    meta: "(907) 821-6502",
  },
  {
    title: "Fish Boxes",
    price: "$15 / box",
    body: "Sturdy waxed boxes ready for the flight home, sized for airline check-in.",
  },
  {
    title: "Island Transportation",
    price: "$175–$200 per vehicle · one way",
    body: "Klawock to CC Lodging $175 · Hollis to Lodging $200. Reserve in advance with your arrival details.",
  },
  {
    title: "Medivac Insurance",
    price: "Recommended add-on",
    body: "Peace of mind for remote Alaska travel. We recommend Guardian Flight or LifeMed Alaska for medivac coverage.",
    links: [
      { href: "https://www.guardianflight.com/", label: "Guardian Flight →" },
      { href: "https://lifemedalaska.com/", label: "LifeMed Alaska →" },
    ],
  },
];

const RENTALS = [
  {
    img: boatSkiffAsset.url,
    name: "Boat",
    price: "$400 / day + fuel",
    features: [
      "20' / 22' welded aluminum Hewescraft",
      "Downriggers, GPS / sonar, VHF radio",
      "Survival suits & safety gear",
      "Ready for a full day offshore",
    ],
  },
  {
    img: skiffAsset.url,
    name: "Skiff",
    price: "$100 / day",
    features: [
      "16ft welded aluminum skiffs",
      "Bait, ice & a tank of gas",
      "VHF radio & safety gear",
      "Great for protected-water fishing",
    ],
  },
];

function Accommodations() {
  return (
    <div>
      <section className="bg-[var(--color-deep)] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-20 pb-16">
          <p className="uppercase tracking-[0.3em] text-xs text-white/70">Accommodations</p>
          <h1 className="font-display text-5xl md:text-6xl mt-3 max-w-3xl text-balance text-white">
            Comfortable rooms. Capable boats. Honest pricing.
          </h1>
          <p className="mt-5 max-w-2xl text-white/90">
            Everything you need to fish Southeast Alaska on your own schedule — self-guided, without
            the price tag of a guided charter operation.
          </p>
          <p className="mt-4">
            <Link
              to="/fishing"
              className="inline-flex items-center gap-1 text-[var(--color-ember)] underline underline-offset-4 hover:text-white"
            >
              See what you'll be fishing for →
            </Link>
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 -mt-12 pb-16">
        <div className="grid gap-6 md:grid-cols-3">
          {PACKAGES.map((p) => (
            <article
              key={p.title}
              className="rounded-lg overflow-hidden bg-card border border-border flex flex-col"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={p.img}
                  alt={p.title}
                  loading="lazy"
                  width={1280}
                  height={900}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <h2 className="font-display text-2xl">{p.title}</h2>
                <p className="text-[var(--color-ember)] font-semibold mt-1">{p.price}</p>
                <ul className="mt-4 space-y-2 text-sm text-muted-foreground flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-2">
                      <span className="text-[var(--color-spruce)]">✦</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className="mt-6 text-sm font-semibold uppercase tracking-wider text-primary hover:text-[var(--color-ember)]"
                >
                  Reserve →
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Skiff & Boat Rentals */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-16">
        <div>
          <div>

            <p className="uppercase tracking-[0.3em] text-xs text-[var(--color-ember)]">
              Skiff & Boat Rentals
            </p>
            <h2 className="font-display text-3xl md:text-4xl mt-2">
              Pick your ride for the day
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Hover over each image to see features and pricing. Check the calendar to
              the left for open dates before you request.
            </p>
          </div>
        </div>


        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {RENTALS.map((r) => (
            <article
              key={r.name}
              className="group relative rounded-lg overflow-hidden border border-border bg-card"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={r.img}
                  alt={r.name}
                  loading="lazy"
                  width={1600}
                  height={1000}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="font-display text-2xl text-white">{r.name}</h3>
                  <p className="text-[var(--color-ember)] font-semibold">{r.price}</p>
                </div>
                <ul className="mt-3 grid gap-1.5 text-sm text-white/90 max-h-0 opacity-0 overflow-hidden transition-all duration-500 group-hover:max-h-64 group-hover:opacity-100 group-focus-within:max-h-64 group-focus-within:opacity-100">
                  {r.features.map((f) => (
                    <li key={f} className="flex gap-2">
                      <span className="text-[var(--color-ember)]">✦</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-8 rounded-lg border border-border bg-card p-6 text-sm text-card-foreground shadow-sm">
          <p className="font-semibold text-foreground">Prices are per person per day — based on double occupancy.</p>
          <p className="mt-1 text-muted-foreground">Reservations assigned based on group size.</p>
          <p className="mt-4 font-semibold text-foreground">Items not included in boat rentals:</p>
          <ul className="mt-2 grid gap-1 text-foreground/80 sm:grid-cols-2">
            <li>• Fuel</li>
            <li>• Fishing licenses</li>
            <li>• Bait & tackle</li>
            <li>• Additional fish processing and box</li>
            <li>• Gratuities</li>
          </ul>
          <p className="mt-4 text-xs text-muted-foreground">*Prices are subject to change.</p>
        </div>

      </section>

      {/* Services & add-ons */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-24">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="uppercase tracking-[0.3em] text-xs text-[var(--color-ember)]">
              Services & Add-Ons
            </p>
            <h2 className="font-display text-3xl md:text-4xl mt-2">
              Everything else you'll need on the island
            </h2>
          </div>
          <Link
            to="/contact"
            className="text-sm font-semibold uppercase tracking-wider text-primary hover:text-[var(--color-ember)]"
          >
            Submit an inquiry →
          </Link>
        </div>

        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s) => (
            <article
              key={s.title}
              className="rounded-lg border border-border bg-card p-6 flex flex-col"
            >
              <h3 className="font-display text-xl">{s.title}</h3>
              <p className="text-[var(--color-ember)] font-semibold mt-1">{s.price}</p>
              <p className="mt-3 text-sm text-muted-foreground flex-1">{s.body}</p>
              {s.meta && (
                <p className="mt-4 text-sm font-medium text-foreground">
                  <a
                    href={telHref(s.meta)}
                    aria-label={callAriaLabel(s.meta, s.title)}
                    className="inline-flex min-h-11 items-center hover:text-[var(--color-ember)]"
                  >
                    {s.meta}
                  </a>
                </p>
              )}
              {s.links && (
                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-sm font-semibold uppercase tracking-wider">
                  {s.links.map((l) => (
                    <a
                      key={l.href}
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-[var(--color-ember)]"
                    >
                      {l.label}
                    </a>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>

        <div className="mt-16 rounded-xl bg-[var(--color-parchment)] border border-border p-10">
          <h2 className="font-display text-3xl">What's included on the water</h2>
          <div className="mt-6 grid sm:grid-cols-2 gap-y-3 gap-x-10 text-sm">
            {[
              "20' / 22' welded aluminum Hewescraft boats & 16ft welded aluminum skiffs",
              "Downriggers with cannonballs",
              "GPS / fishfinder & VHF radio",
              "Survival suits & PFDs for every angler",
              "Charts & local fishing intel",
              "Bait — herring, squid, salmon bellies",
              "Daily safety briefing",
              "Cleaning station & ice at the dock",
            ].map((s) => (
              <p key={s} className="flex gap-2">
                <span className="text-[var(--color-ember)]">●</span> {s}
              </p>
            ))}
          </div>
        </div>

        <p className="mt-8 text-xs text-muted-foreground text-center">
          Gratuities appreciated but not included · We reserve the right to refuse service · Prices are subject to change
        </p>
      </section>
    </div>
  );
}
